import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';

interface NavItem {
  name: string;
  path?: string;
  submenu?: { name: string; path: string }[];
}

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOurWorkOpen, setIsOurWorkOpen] = useState(false);
  const [isOurWorkOpenMobile, setIsOurWorkOpenMobile] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOurWorkOpen(false);
      }
    };

    if (isOurWorkOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOurWorkOpen]);

  const navItems: NavItem[] = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    // { name: 'How It Works', path: '/how-it-works' },
    { name: 'Clients', path: '/clients' },
    { 
      name: 'Our Work', 
      // path: '/our-work',
      submenu: [
        { name: 'Graphic Design', path: '/our-work' },
        { name: 'Video & Reels', path: '/videos-reels' }
      ]
    },
    { name: 'Testimonials', path: '/testimonials' },
    // { name: 'Offers', path: '/offers' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleLinkClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
      style={{ overflow: 'visible' }}
    >
      <div className="container-custom" style={{ overflow: 'visible' }}>
        <div className="flex items-center justify-between h-20 relative" style={{ overflow: 'visible' }}>
          {/* Logo */}
          <Link to="/" onClick={handleLinkClick} className="flex items-center space-x-2">
            <img src="/Color-Drop-business-solutions-Logo.png" alt="Color Drop" width={130} height={130} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" style={{ position: 'relative', overflow: 'visible' }}>
            {navItems.map((item) => (
              item.submenu ? (
                <div
                  key={item.name}
                  ref={item.name === 'Our Work' ? dropdownRef : null}
                  className="relative"
                  style={{ overflow: 'visible' }}
                  onMouseEnter={() => setIsOurWorkOpen(true)}
                  onMouseLeave={() => setIsOurWorkOpen(false)}
                >
                  <Link
                    to={item.path || '#'}
                    onClick={handleLinkClick}
                    className={`font-medium transition-colors duration-200 flex items-center space-x-1 ${
                      location.pathname === item.path
                        ? 'text-primary-600'
                        : 'text-neutral-700 hover:text-primary-600'
                    }`}
                  >
                    <span>{item.name}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOurWorkOpen ? 'rotate-180' : ''}`} />
                  </Link>
                  
                  <AnimatePresence>
                    {isOurWorkOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-neutral-200 py-2"
                        style={{ 
                          position: 'absolute',
                          zIndex: 9999,
                          overflow: 'visible'
                        }}
                      >
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            onClick={handleLinkClick}
                            className={`block px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                              location.pathname === subItem.path
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-neutral-700 hover:text-primary-600 hover:bg-neutral-50'
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path || '#'}
                  onClick={handleLinkClick}
                  className={`font-medium transition-colors duration-200 ${
                    location.pathname === item.path
                      ? 'text-primary-600'
                      : 'text-neutral-700 hover:text-primary-600'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          {/* <div className="hidden lg:block">
            <Link to="/contact" className="btn-primary">
              Get Quote
            </Link>
          </div> */}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-neutral-100 transition-colors duration-200"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white border-t border-neutral-200"
            >
              <nav className="py-4 space-y-2">
                {navItems.map((item) => (
                  item.submenu ? (
                    <div key={item.name}>
                      <button
                        onClick={() => setIsOurWorkOpenMobile(!isOurWorkOpenMobile)}
                        className={`w-full flex items-center justify-between px-4 py-3 font-medium transition-colors duration-200 ${
                          location.pathname === item.path
                            ? 'text-primary-600 bg-primary-50'
                            : 'text-neutral-700 hover:text-primary-600 hover:bg-neutral-50'
                        }`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOurWorkOpenMobile ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {isOurWorkOpenMobile && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="pl-8 space-y-1">
                              {item.submenu.map((subItem) => (
                                <Link
                                  key={subItem.name}
                                  to={subItem.path}
                                  onClick={() => {
                                    setIsMenuOpen(false);
                                    setIsOurWorkOpenMobile(false);
                                    handleLinkClick();
                                  }}
                                  className={`block px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                                    location.pathname === subItem.path
                                      ? 'text-primary-600 bg-primary-50'
                                      : 'text-neutral-600 hover:text-primary-600 hover:bg-neutral-50'
                                  }`}
                                >
                                  {subItem.name}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.path || '#'}
                      onClick={() => {
                        setIsMenuOpen(false);
                        handleLinkClick();
                      }}
                      className={`block px-4 py-3 font-medium transition-colors duration-200 ${
                        location.pathname === item.path
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-neutral-700 hover:text-primary-600 hover:bg-neutral-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )
                ))}
                {/* <div className="px-4 pt-4">
                  <Link
                    to="/contact"
                    onClick={() => setIsMenuOpen(false)}
                    className="btn-primary w-full text-center block"
                  >
                    Get Quote
                  </Link>
                </div> */}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
